import type { ObraMembresia } from "@/lib/auth";
import type { OutboxMutation, OutboxStatus } from "@/lib/outbox";

// Presentación del centro de conflictos (F4 #53): traduce el veredicto del servidor y la intención
// del técnico a texto en es-ES. El servidor es la única autoridad; aquí solo se DESCRIBE, nunca se
// auto-resuelve.

/**
 * nombreObra resuelve el nombre visible de la obra a la que pertenece una mutación (F7 #162), a
 * partir de las obras del usuario (`me.obras`). La cola offline es UNA FIFO global que sirve
 * varias obras, así que el centro de sincronización etiqueta cada ítem con la suya. Si el id no
 * está entre las obras del usuario (obra ajena, o membresía revocada tras encolar sin red),
 * degrada a un marcador «Obra #N» para no ocultar el trabajo pendiente.
 */
export function nombreObra(obras: ObraMembresia[] | undefined, idObra: number): string {
  const obra = obras?.find((o) => o.id_obra === idObra);
  return obra ? obra.nombre : `Obra #${idObra}`;
}

/**
 * describeIntencion redacta «la intención del técnico» de una mutación. Usa el `resumen`
 * denormalizado al encolar (robusto: no depende de cachés que el logout pudo purgar) y degrada a
 * una descripción por (entity, op) si faltara.
 */
export function describeIntencion(m: OutboxMutation): string {
  if (m.resumen && m.resumen.trim() !== "") {
    return m.resumen;
  }
  if (m.entity === "loto" && m.op === "create") {
    return "Abrir un LOTO";
  }
  if (m.entity === "bloqueo" && m.op === "create") {
    return "Colocar un candado";
  }
  if (m.entity === "bloqueo" && m.op === "retirar") {
    return "Retirar un candado";
  }
  if (m.entity === "bloqueo" && m.op === "retirar_punto") {
    return "Retirar un punto de un candado";
  }
  return `${m.entity}/${m.op}`;
}

// La redacción del veredicto del servidor (`traducirEstadoServidor`) vivía aquí hasta #306. Bajó a
// `lib/sync-estado.ts` porque la ficha del LOTO también la necesita, para cerrar con un aviso la
// ficha temporal de un alta rechazada: `features/*` usa `lib/*`, nunca entre features. Sin
// re-export de compatibilidad (misma decisión que #383): una sola ruta para el símbolo.

/** etiquetaEstado da la etiqueta corta (badge) de cada estado de una mutación de la outbox. */
export function etiquetaEstado(status: OutboxStatus): string {
  switch (status) {
    case "pending":
      return "Pendiente";
    case "conflict":
      return "Conflicto";
    case "rejected":
      return "Rechazada";
    case "blocked":
      return "Bloqueada";
    case "applied":
      return "Aplicada";
  }
}

/** claseEstado da las clases del badge por estado (mismos colores que el resto de la app). */
export function claseEstado(status: OutboxStatus): string {
  switch (status) {
    case "pending":
      return "bg-primary-100 text-primary-800";
    case "conflict":
      return "bg-amber-100 text-amber-800";
    case "rejected":
      return "bg-red-100 text-red-800";
    case "blocked":
      return "bg-slate-200 text-slate-700";
    case "applied":
      return "bg-emerald-100 text-emerald-800";
  }
}
